import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./App.css";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import Breadcrumbs from "./components/Breadcrumbs";
import LoadingSpinner from "./components/LoadingSpinner";
import { useAuth } from "./contexts/AuthContext";

type LazyWithPreload = React.LazyExoticComponent<React.ComponentType<any>> & {
  preload: () => Promise<any>;
};

const lazyWithPreload = (
  factory: () => Promise<{ default: React.ComponentType<any> }>,
): LazyWithPreload => {
  const Component = lazy(factory) as LazyWithPreload;
  Component.preload = factory;
  return Component;
};

const HomePage = lazyWithPreload(() => import("./pages/HomePage"));
const PhotoGalleryPage = lazyWithPreload(
  () => import("./pages/PhotoGalleryPage"),
);
const CatalogPage = lazyWithPreload(() => import("./pages/CatalogPage"));
const TravelDetailPage = lazyWithPreload(
  () => import("./pages/TravelDetailPage"),
);
const AboutPage = lazyWithPreload(() => import("./pages/AboutPage"));
const ContactPage = lazyWithPreload(() => import("./pages/ContactPage"));
const JoinClubPage = lazyWithPreload(() => import("./pages/JoinClubPage"));
const LoginPage = lazyWithPreload(() => import("./pages/LoginPage"));
const AdminDashboard = lazyWithPreload(() => import("./pages/AdminDashboard"));
const AddTravelPage = lazyWithPreload(() => import("./pages/AddTravelPage"));
const EditTravelPage = lazyWithPreload(() => import("./pages/EditTravelPage"));
const UserManagementPage = lazyWithPreload(
  () => import("./pages/UserManagementPage"),
);
const SettingsPage = lazyWithPreload(() => import("./pages/SettingsPage"));

const preloadedPaths = new Set<string>();

const preloadRouteForPath = (pathname: string) => {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (preloadedPaths.has(normalized)) return;

  const preload = (() => {
    if (normalized === "/") return HomePage.preload;
    if (normalized === "/gallery") return PhotoGalleryPage.preload;
    if (normalized === "/catalog") return CatalogPage.preload;
    if (normalized === "/about") return AboutPage.preload;
    if (normalized === "/contact") return ContactPage.preload;
    if (normalized === "/join-club") return JoinClubPage.preload;
    if (normalized === "/login") return LoginPage.preload;
    if (normalized.startsWith("/travel/")) return TravelDetailPage.preload;
    if (normalized === "/admin") return AdminDashboard.preload;
    if (normalized === "/admin/add-travel") return AddTravelPage.preload;
    if (normalized.startsWith("/admin/edit-travel/")) {
      return EditTravelPage.preload;
    }
    if (normalized === "/admin/users") return UserManagementPage.preload;
    if (normalized === "/admin/settings") return SettingsPage.preload;
    return null;
  })();

  if (!preload) return;
  preloadedPaths.add(normalized);
  preload().catch(() => {
    preloadedPaths.delete(normalized);
  });
};

// Component to scroll to top on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const App: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";
  const isAdminPath = location.pathname.startsWith("/admin");

  // Track visit once when app loads (only for non-logged-in users in production)
  useEffect(() => {
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    if (!isLocalhost && !isAuthenticated && !isLoginPage) {
      fetch("/api/track-visit.php", { method: "POST" }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    preloadRouteForPath(location.pathname);

    let idleHandle: number | null = null;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

    if ("requestIdleCallback" in window) {
      idleHandle = window.requestIdleCallback(() => {
        preloadRouteForPath("/catalog");
        preloadRouteForPath("/about");
        preloadRouteForPath("/contact");
        preloadRouteForPath("/gallery");
      });
    } else {
      timeoutHandle = globalThis.setTimeout(() => {
        preloadRouteForPath("/catalog");
        preloadRouteForPath("/about");
        preloadRouteForPath("/contact");
        preloadRouteForPath("/gallery");
      }, 1200);
    }

    const onHoverPrefetch = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      try {
        const url = new URL(anchor.href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        preloadRouteForPath(url.pathname);
      } catch {
        // Ignore malformed href values.
      }
    };

    document.addEventListener("mouseover", onHoverPrefetch, {
      passive: true,
    });

    return () => {
      if (timeoutHandle !== null) {
        globalThis.clearTimeout(timeoutHandle);
      }
      if (idleHandle !== null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleHandle);
      }
      document.removeEventListener("mouseover", onHoverPrefetch);
    };
  }, [location.pathname]);

  return (
    <div className="App">
      {isAdminPath && (
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      )}
      <ScrollToTop />
      {!isLoginPage && <Navbar />}
      {!isLoginPage && isAdminPath && <Breadcrumbs variant="standard" />}

      <Suspense
        fallback={
          <div
            style={{ minHeight: "40vh", display: "grid", placeItems: "center" }}
          >
            <LoadingSpinner size="large" />
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/gallery" element={<PhotoGalleryPage />} />
          <Route path="/catalog" element={<CatalogPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/join-club" element={<JoinClubPage />} />
          <Route path="/travel/:id" element={<TravelDetailPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/add-travel"
            element={
              <ProtectedRoute requireAdmin>
                <AddTravelPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/edit-travel/:id"
            element={
              <ProtectedRoute requireAdmin>
                <EditTravelPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requireAdmin>
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute requireAdmin>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>

      {!isAuthenticated && <Footer />}
    </div>
  );
};

export default App;
